import { useState, type CSSProperties } from 'react';
import { Check, Copy, ExternalLink, Gift, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import type { ParticipantCoupon } from '../api/participant-api';
import { useClaimCoupon } from '../api/use-participant';
import { expiryText } from '../model/coupons';

/** "Resgatar cupom": o código só passa a ser do participante no clique. */
export function ClaimCouponCard({
  idPassbook,
  accentColor,
  foregroundColor,
}: {
  idPassbook: string;
  accentColor?: string;
  foregroundColor?: string;
}) {
  const claim = useClaimCoupon(idPassbook);
  const accent: CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
    : undefined;

  const redeem = () => {
    claim.mutate(undefined, {
      onSuccess: () => toast.success('Cupom resgatado! O código já está aqui para você usar.'),
      onError: (err) => toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível resgatar agora.'),
    });
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-4 p-5">
        <span
          className={cn('grid size-12 shrink-0 place-items-center rounded-2xl', !accent && 'bg-primary text-primary-foreground')}
          style={accent}
        >
          <Gift className="size-6" />
        </span>
        <div className="min-w-0 space-y-1">
          <p className="font-semibold">Você tem um cupom para resgatar</p>
          <p className="text-sm text-muted-foreground">Resgate para receber o código e usar no site.</p>
        </div>
      </div>
      <div className="px-5 pb-5">
        <Button
          type="button"
          size="lg"
          className="w-full hover:opacity-90"
          style={accent}
          onClick={redeem}
          loading={claim.isPending}
        >
          Resgatar cupom
        </Button>
      </div>
    </Card>
  );
}

/**
 * Cupom ativo: faixa na cor da loja (campanha + benefício), código grande com
 * "Copiar", validade e "Usar no site". O MESMO card na página de cupons e no Início.
 */
export function ActiveCouponCard({
  coupon,
  accentColor,
  foregroundColor,
}: {
  coupon: ParticipantCoupon;
  accentColor?: string;
  foregroundColor?: string;
}) {
  const [copied, setCopied] = useState(false);
  const expiry = expiryText(coupon.expiresAt);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      toast.success('Código copiado.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar. Selecione o código e copie.');
    }
  };

  const accent: CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
    : undefined;

  return (
    <Card className="overflow-hidden">
      <div className={cn('px-5 py-4', !accent && 'bg-primary text-primary-foreground')} style={accent}>
        <p className="text-xs font-medium uppercase tracking-wider opacity-80">{coupon.campaignName || 'Seu cupom'}</p>
        <p className="text-xl font-semibold">{coupon.benefit || 'Benefício exclusivo'}</p>
      </div>
      <div className="space-y-4 p-5">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-border bg-muted/40 px-4 py-3">
          <code className="select-all break-all font-mono text-lg font-semibold tracking-wider">{coupon.code}</code>
          <Button type="button" variant="ghost" size="sm" onClick={() => void copy()} aria-label="Copiar código">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
        {expiry ? <p className="text-sm text-muted-foreground">{expiry}</p> : null}
        {coupon.redeemUrl ? (
          <Button asChild size="lg" className="w-full hover:opacity-90" style={accent}>
            <a href={coupon.redeemUrl} target="_blank" rel="noopener noreferrer">
              Usar no site
              <ExternalLink className="size-4" />
            </a>
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Use este código no carrinho, ao finalizar a compra no site.</p>
        )}
      </div>
    </Card>
  );
}

/** Sem cupom nenhum: o mesmo estado vazio na página de cupons e no Início. */
export function EmptyCoupons({ className }: { className?: string }) {
  return (
    <Card className={cn('flex flex-col items-center gap-2 p-8 text-center', className)}>
      <span className="grid size-12 place-items-center rounded-2xl bg-muted">
        <Ticket className="size-6 text-muted-foreground" />
      </span>
      <p className="font-medium">Você ainda não tem cupons</p>
      <p className="max-w-xs text-sm text-muted-foreground">
        Quando você ganhar um cupom, ele aparece aqui com o código para usar no site.
      </p>
    </Card>
  );
}
